import { UserX } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { HANDOVER_KIND_LABELS, type HandoverKind } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { PersonPicker } from '../../../../components/PersonPicker';
import { Segmented } from '../../../../components/Segmented';
import { useToasts } from '../../../../stores/toasts';
import { useColleagues, useDeactivateUser, type UserDetail } from '../../api/users-api';
import { deactivateBody, isHandoverComplete, ownedKinds, type HandoverMode } from './people-address';
import { countOf, describeOwned, firstNameOf } from './people-format';
import styles from './DeactivateConfirm.module.css';

/**
 * F2.05 — deactivate, with the hand-over in the same step, opened beside its button (never centred, never an
 * Undo: reactivating them later does not give the work back).
 *
 * One person takes everything by default, their manager suggested. "Split by kind of work" asks for a person per
 * kind they actually have — hire requests as recruiter, jobs as recruiter, jobs as line manager, tasks — each starting on the
 * one person, so only the kinds that differ need a click.
 */
export function DeactivateConfirm({
  user,
  onDone,
  onCancel,
}: {
  readonly user: UserDetail;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}): ReactNode {
  const colleagues = useColleagues();
  const deactivate = useDeactivateUser(user.id);
  const showToast = useToasts((state) => state.show);

  // Never themselves, and never somebody who can't sign in (`/users/colleagues` is active people only).
  const people = (colleagues.data ?? []).filter((person) => person.id !== user.id);
  const manager = people.find((person) => person.id === user.managerUserId) ?? null;
  const kinds = ownedKinds(user.ownedRecords);
  const first = firstNameOf(user.fullName);

  const [mode, setMode] = useState<HandoverMode>('one');
  const [onePerson, setOnePerson] = useState<string | null>(null);
  const [perKind, setPerKind] = useState<Partial<Record<HandoverKind, string | null>>>({});
  // The manager is the suggestion until somebody else is chosen.
  const chosen = onePerson ?? manager?.id ?? null;
  const isReady = isHandoverComplete(user.ownedRecords, mode, chosen, perKind);

  const submit = (): void => {
    deactivate.mutate(deactivateBody(user.ownedRecords, mode, chosen, perKind), {
      onSuccess: () => {
        showToast({ tone: 'success', message: `${user.fullName} deactivated`, detail: 'They were signed out everywhere.' });
        onDone();
      },
      onError: (error) => showToast({ tone: 'danger', message: error.message }),
    });
  };

  return (
    <div className={styles.confirm}>
      <h3 className={styles.title}>Deactivate {user.fullName}?</h3>
      <p className={styles.body}>
        {first} is signed out everywhere and can't sign in again.{' '}
        {kinds.length === 0
          ? `${first} owns nothing that needs handing over.`
          : `${first} has ${describeOwned(user.ownedRecords)}. That work moves to whoever you choose below, then ${first} is deactivated. Hire requests ${first} raised stay raised by them.`}
      </p>

      {kinds.length > 1 && (
        <Segmented<HandoverMode>
          label="Who takes over"
          value={mode}
          options={[
            { value: 'one', label: 'One person' },
            { value: 'split', label: 'Split by kind of work' },
          ]}
          onChange={setMode}
        />
      )}

      {kinds.length > 0 && (
        <Field
          label={mode === 'split' ? 'Unless changed below, the work goes to' : 'Who takes over the work'}
          hint={manager && chosen === manager.id ? `${manager.fullName} is ${first}'s manager.` : undefined}
          isRequired={mode === 'one'}
        >
          {({ inputId, describedBy }) => (
            <PersonPicker
              id={inputId}
              describedBy={describedBy}
              label="Who takes over the work"
              people={people}
              value={chosen}
              onChange={setOnePerson}
              placeholder="Choose a colleague"
            />
          )}
        </Field>
      )}

      {mode === 'split' && kinds.length > 1 && (
        <ul className={styles.kinds} aria-label="Who takes each kind of work">
          {kinds.map((kind) => (
            <li key={kind} className={styles.kind}>
              <Field label={`${HANDOVER_KIND_LABELS[kind]} (${countOf(kind, user.ownedRecords[kind])})`}>
                {({ inputId }) => (
                  <PersonPicker
                    id={inputId}
                    label={HANDOVER_KIND_LABELS[kind]}
                    people={people}
                    value={perKind[kind] ?? chosen}
                    onChange={(next) => setPerKind((current) => ({ ...current, [kind]: next }))}
                    placeholder="Choose a colleague"
                  />
                )}
              </Field>
            </li>
          ))}
        </ul>
      )}

      <p className={styles.note}>You can reactivate {first} later, but the work stays with whoever takes it now.</p>

      <div className={styles.actions}>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="dangerSolid" icon={UserX} disabled={!isReady} isPending={deactivate.isPending} onClick={submit}>
          {kinds.length === 0 ? 'Deactivate' : 'Move work and deactivate'}
        </Button>
      </div>
    </div>
  );
}
